import { useState } from "react";

import { grammarFigureUrl, type GrammarEntry, type GrammarExample } from "@/lib/search";
import { FormationBlock, NotesBody } from "@/surfaces/grammar/GrammarBlocks";
import { notesBlocks } from "@/surfaces/grammar/grammarNotes";

import { KindGutter } from "./KindGutter";
import { citation, relatedChips } from "./resultKind";

/**
 * A DoJG entry, as a result row and expanded in place (日本-498).
 *
 * `ResultCard`'s shape deliberately: same panel, same badge row, same button in the same corner, so
 * a grammar row reads as another row rather than as another feature. What differs is what a grammar
 * point has and a word does not — 接続, examples chosen by the book, and a citation.
 *
 * **The corner is `開く`, and it used to be `+ 追加` / `編集`** (日本-519). Grammar stopped being a
 * card type, so the row's action is to open the entry in 文法 rather than to hold it — and with it
 * went the 登録済 / 未登録 badge, which asked a question the collection can no longer answer.
 *
 * Expanding still happens in place, so the dock can show a construction without leaving the
 * sentence; 開く is for when you want the book.
 */

interface Props {
  entry: GrammarEntry;
  focused: boolean;
  expanded: boolean;
  busy?: boolean;
  isPhone?: boolean;
  onFocus: () => void;
  onToggle: () => void;
  /** Open this entry in 文法, deep-linked by its DoJG id (日本-519). */
  onOpen: () => void;
}

/** The book's Key Sentences, which it labels — so leading with one is a rule, not a judgement. */
function keySentences(examples: GrammarExample[]): GrammarExample[] {
  const keys = examples.filter((x) => x.label.startsWith("ks"));
  return keys.length > 0 ? keys : examples.slice(0, 1);
}

/**
 * Mark the construction inside its example.
 *
 * `pattern` holds the span AS INFLECTED in this sentence, which is the whole reason it is carried —
 * `あげる (1)` gives `あげた` and `あげました`, and no string match on the concept would find either.
 *
 * Split rather than regex, on `splitOnTarget`'s rule: the span is arbitrary text off the dataset,
 * so `String.prototype.split` takes it literally and a `(` in a pattern can never become one.
 */
function marked(text: string, patterns: string[]) {
  const span = patterns.find((p) => p && text.includes(p));
  if (!span) return text;
  const parts = text.split(span);
  return parts.flatMap((part, index) =>
    index === 0
      ? [part]
      : [
          <span key={index} data-check="grammar.pattern" className="text-[color:var(--accent-text)]">
            {span}
          </span>,
          part,
        ],
  );
}

function ExampleLine({ example, compact }: { example: GrammarExample; compact: boolean }) {
  return (
    <div data-check="grammar.example" className="flex flex-col gap-[3px]">
      <span className={`font-jp text-ink-3 ${compact ? "text-[15px]/[1.6]" : "text-[17px]/[1.6]"}`}>
        {marked(example.ja, example.pattern)}
      </span>
      {/* The English stays here, in 検索, and is what the CARD drops (Alex, 27 Aug 2026) — five
          bare Japanese lines read as a paradigm where five ja/en pairs read as a page. */}
      {example.en && <span className="text-meta text-ink-4">{example.en}</span>}
    </div>
  );
}

export function GrammarCard({
  entry,
  focused,
  expanded,
  busy = false,
  isPhone = false,
  onFocus,
  onToggle,
  onOpen,
}: Props) {
  const [showAll, setShowAll] = useState(false);
  const chips = relatedChips(entry.related);
  const shown = expanded && showAll ? entry.examples : keySentences(entry.examples).slice(0, 1);
  // **`length - 1`, not `length - shown.length`.** The second reads correctly and is wrong: once
  // the fold is open it answers 0, the toggle stops rendering and there is no way back to one
  // example. What the number means is *how many the fold holds back when it is closed*, which does
  // not depend on whether it is open — `CardFace`'s own fold says the same thing.
  const rest = Math.max(entry.examples.length - 1, 0);

  return (
    <div
      data-check="result.grammar"
      data-grammar-id={entry.id}
      className={[
        "mr-3 flex overflow-hidden rounded-[var(--r-3)] bg-panel",
        focused
          ? "border border-accent-line shadow-[var(--sh-frame)]"
          : "border border-line",
      ].join(" ")}
    >
      <KindGutter kind="grammar" />

      <div
        role="button"
        tabIndex={-1}
        onMouseEnter={onFocus}
        onClick={onToggle}
        className={`flex min-w-0 flex-1 cursor-pointer flex-col gap-2 px-4 text-left ${
          focused ? "py-[13px]" : "py-3"
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-[10px]">
            <span
              data-check="grammar.concept"
              className={`font-jp font-semibold ${focused ? "text-[27px]/[1.1]" : "text-[25px]/[1.1]"}`}
            >
              {entry.concept}
            </span>
            {entry.partOfSpeech && (
              <span className="text-meta text-ink-3">{entry.partOfSpeech}</span>
            )}
            {/* The volume stays English — it names a real edition, like `Genki (Apoc)` does. */}
            <span className="rounded-[var(--r-2)] border border-line bg-void px-[7px] py-[3px] font-mono text-meta font-semibold text-ink-2">
              {citation(entry)}
            </span>
          </div>
          {/* **開く, and the corner used to hold `+ 追加` / `編集`** (日本-519). A grammar entry is
              not something the collection can hold, so the row's action is to go and read it —
              which is also what puts `O` on screen, this repo's rule being that a control
              advertising a key has to exist and a key with no control is undiscoverable. */}
          <button
            type="button"
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              onOpen();
            }}
            data-check="grammar.open"
            className="shrink-0 rounded-[var(--r-2)] border border-accent-line bg-void px-3 py-2 text-[14px]/[1] font-semibold text-accent disabled:opacity-60"
          >
            開く
            {!isPhone && <span className="ml-[7px] font-mono text-[14px]/[1] opacity-75">O</span>}
          </button>
        </div>

        {entry.meaning && <p className="m-0 text-body text-ink">{entry.meaning}</p>}

        {expanded && entry.usage && <p className="m-0 text-meta text-ink-3">{entry.usage}</p>}

        {/* **The same 接続 the 文法 surface draws** (日本-524). It was `{entry.formation}` in a
            `<span>` here — the deck's table with every cell boundary deleted, in one line. CLAUDE.md's
            rule for this pair is that whatever 文法 gains reaches Read, so both call `FormationBlock`
            and there is no second implementation to drift. */}
        {expanded && entry.formation && (
          <div className="flex gap-[13px]">
            <span className="shrink-0 font-jp text-meta font-semibold tracking-[.1em] text-ink-4">
              接続
            </span>
            <div className="min-w-0 flex-1">
              <FormationBlock formation={entry.formation} />
            </div>
          </div>
        )}

        {shown.length > 0 && (
          <div className="flex gap-[13px] border-l-2 border-line pl-[14px]">
            <span className="shrink-0 font-jp text-meta font-semibold tracking-[.1em] text-ink-4">
              例文
            </span>
            <div className="flex min-w-0 flex-col gap-[9px]">
              {shown.map((example, index) => (
                <ExampleLine key={index} example={example} compact={!expanded} />
              ))}
              {expanded && rest > 0 && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setShowAll((current) => !current);
                  }}
                  data-check="grammar.examples.more"
                  className="self-start rounded-[var(--r-2)] border border-line-strong bg-line-soft px-[10px] py-[5px] font-jp text-meta font-semibold text-ink-3"
                >
                  {showAll ? "閉じる" : `＋${rest}例`}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Label left, like 接続 / 例文 / 関連 above it — four blocks in one row is what makes the
            expanded entry read as one card rather than as four stacked ones. The label column is
            `shrink-0` so a 1,500-character notes block cannot squeeze it. */}
        {/* **Parsed and set, not a `pre-line` paragraph** (日本-524) — the same `NotesBody` 文法
            draws. The dock is a ~700px column, so the measure is already short here; what this
            buys is the structure, which was invisible when every line was one style. */}
        {expanded && entry.notesText && (
          <div className="flex gap-[13px]">
            <span className="shrink-0 font-jp text-meta font-semibold tracking-[.1em] text-ink-4">
              解説
            </span>
            <NotesBody blocks={notesBlocks(entry.notesText)} className="min-w-0 flex-1" />
          </div>
        )}

        {expanded && entry.figures.length > 0 && (
          <div className="flex flex-col gap-2">
            {entry.figures.map((figure) => (
              <figure key={figure.file} className="m-0 flex flex-col gap-1">
                <img
                  src={grammarFigureUrl(figure.file)}
                  alt={figure.caption ?? ""}
                  className="max-w-full rounded-[var(--r-2)] border border-line"
                />
                {figure.caption && (
                  <figcaption className="text-meta text-ink-4">{figure.caption}</figcaption>
                )}
              </figure>
            ))}
          </div>
        )}

        {expanded && chips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-jp text-meta font-semibold tracking-[.1em] text-ink-4">関連</span>
            {chips.map((chip) => (
              <span
                key={chip}
                className="rounded-[var(--r-2)] border border-line-strong bg-line-soft px-[10px] py-[5px] font-jp text-meta text-ink-2"
              >
                {chip}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
